import { useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { WalletReadyState } from '@solana/wallet-adapter-base';

/**
 * Turns the dead-end "wallet not connected" guard into an actionable connect
 * attempt.
 *
 * `autoConnect` (eager connect) silently gives up when the wallet extension is
 * locked, still mid-connect, or was dropped on a route change — leaving
 * `connected` false even though the user has a wallet selected. The old guards
 * just showed "Please connect your wallet" and bailed, with no way forward,
 * which is what users reported as "wallet not connected".
 *
 * `ensureReady()` returns true when the wallet is usable right now. Otherwise it
 * *initiates* a connection — retrying `connect()` on the selected wallet (which
 * re-prompts an unlock/approve), or opening the wallet picker when none is
 * chosen — and returns false so the caller can show a short "then tap again"
 * hint and bail. We deliberately do NOT try to use a freshly-connected wallet
 * within the same handler: the captured `publicKey` / `signTransaction` are
 * stale until React re-renders, so the user re-taps once the adapter reports
 * connected (which is immediate after they approve). No RPC endpoint is touched.
 */
export const useWalletReady = () => {
  const {
    connected,
    publicKey,
    signTransaction,
    signMessage,
    wallet,
    connecting,
    connect
  } = useWallet();
  const { setVisible } = useWalletModal();

  // A wallet is only truly usable when its adapter reports Installed. Phantom
  // can be *selected* (persisted from a past session) yet report NotDetected /
  // Loadable here — the extension isn't injecting, or we're on mobile — in
  // which case connect() dead-ends on "download" with no way to switch.
  const walletDetected = wallet?.readyState === WalletReadyState.Installed;

  const ready = Boolean(
    connected && publicKey && (signTransaction || signMessage)
  );

  const ensureReady = useCallback((): boolean => {
    if (connected && publicKey && (signTransaction || signMessage)) return true;

    if (!wallet || !walletDetected) {
      // No wallet chosen, OR the selected wallet isn't actually detected in this
      // browser. Retrying connect() on an undetected wallet just fails with
      // "download"/WalletNotReady and there's no way forward — so open the
      // picker instead, which lets the user switch wallets (e.g. Solflare) or
      // follow the proper install/deep-link flow. This is the "stuck on Phantom
      // saying download, no option to change to Solflare" report.
      setVisible(true);
    } else if (!connecting) {
      // A detected wallet is selected but not connected (locked / eager-connect
      // gave up / dropped). Re-prompt the connection the silent autoConnect
      // skipped.
      connect().catch(() => {
        /* user rejected, or a connect is already in flight — they can retry */
      });
    }
    return false;
  }, [
    connected,
    publicKey,
    signTransaction,
    signMessage,
    wallet,
    walletDetected,
    connecting,
    connect,
    setVisible
  ]);

  const notReadyMessage = walletDetected
    ? 'Connecting your wallet — approve the request in your wallet, then tap again.'
    : 'Select your wallet to continue, then tap again.';

  return { ready, ensureReady, notReadyMessage };
};
